import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { prefetchForRoute } from "@/localization/routePrefetch";

// ── Role → permissions mapping for fine-grained RBAC ─────────────────────────
const ROLE_PERMISSIONS: Record<string, string[]> = {
  SUPER_ADMIN: ["*"],
  COMPANY_ADMIN: [
    "dashboard",
    "revenue",
    "leakages",
    "billing",
    "integrations",
    "settings",
    "team",
    "audit",
    "agent",
    "ai-insights",
    "reports",
    "admin-localization",
    "admin-typography",
    "admin-translation-overrides",
    "admin-region",
    "admin-l10n-health",
    "admin-ai-suggestions",
    "admin-contributions",
    "admin-command-center",
    "admin-usage-analytics",
    "admin-audit-logs",
  ],
  FINANCE_ADMIN: [
    "dashboard",
    "revenue",
    "leakages",
    "billing",
    "audit",
    "agent",
    "ai-insights",
    "reports",
  ],
  ANALYST: ["dashboard", "revenue", "leakages", "ai-insights", "reports"],
  VIEWER: ["dashboard", "revenue"],
};

/** Check whether a role has a specific permission. */
function hasPermission(role: string | undefined, permission: string): boolean {
  if (!role) return false;
  const perms = ROLE_PERMISSIONS[role];
  if (!perms) return false;
  return perms.includes("*") || perms.includes(permission);
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    // ── Auth Layout ──────────────────────────────────────────────────────────
    {
      path: "/auth",
      component: () => import("@/layouts/AuthLayout.vue"),
      children: [
        {
          path: "login",
          name: "login",
          component: () => import("@/views/auth/LoginView.vue"),
        },
        {
          path: "register",
          name: "register",
          component: () => import("@/views/auth/RegisterView.vue"),
        },
        {
          path: "callback",
          name: "auth-callback",
          component: () => import("@/views/auth/CallbackView.vue"),
        },
      ],
    },

    // ── App Layout ────────────────────────────────────────────────────────────
    {
      path: "/",
      component: () => import("@/layouts/AppLayout.vue"),
      meta: { requiresAuth: true },
      children: [
        { path: "", redirect: "/dashboard" },
        {
          path: "dashboard",
          name: "dashboard",
          component: () => import("@/views/DashboardView.vue"),
        },
        {
          path: "agent",
          name: "ai-agent",
          component: () => import("@/views/AgentView.vue"),
          meta: {
            requiresAuth: true,
            requiresRole: ["SUPER_ADMIN", "COMPANY_ADMIN", "FINANCE_ADMIN"],
          },
        },
        {
          path: "ai-insights",
          name: "ai-insights",
          component: () => import("@/views/AIInsightsView.vue"),
          meta: {
            requiresAuth: true,
            requiresRole: [
              "SUPER_ADMIN",
              "COMPANY_ADMIN",
              "FINANCE_ADMIN",
              "ANALYST",
            ],
          },
        },
        {
          path: "revenue",
          name: "revenue",
          component: () => import("@/views/RevenueView.vue"),
        },
        {
          path: "leakages",
          name: "leakages",
          component: () => import("@/views/LeakagesView.vue"),
        },
        {
          path: "billing",
          name: "billing",
          component: () => import("@/views/BillingView.vue"),
        },
        {
          path: "billing/success",
          name: "billing-success",
          component: () => import("@/views/BillingSuccessView.vue"),
        },
        {
          path: "integrations",
          name: "integrations",
          component: () => import("@/views/IntegrationsView.vue"),
        },
        {
          path: "integrations/:id",
          name: "integration-detail",
          component: () => import("@/views/IntegrationDetailView.vue"),
          meta: { requiresAuth: true, permission: "integrations" },
        },
        {
          path: "settings",
          name: "settings",
          component: () => import("@/views/SettingsView.vue"),
        },
        {
          path: "team",
          name: "team",
          component: () => import("@/views/TeamView.vue"),
        },
        {
          path: "audit",
          name: "audit",
          component: () => import("@/views/AuditView.vue"),
          meta: { requiresAdmin: true, permission: "audit" },
        },
        {
          path: "admin/localization",
          name: "admin-localization",
          component: () => import("@/views/AdminLocalizationSettings.vue"),
          meta: {
            requiresAuth: true,
            requiresRole: ["SUPER_ADMIN", "COMPANY_ADMIN"],
          },
        },
        {
          path: "admin/translation-overrides",
          name: "admin-translation-overrides",
          component: () => import("@/views/AdminTranslationOverrides.vue"),
          meta: {
            requiresAuth: true,
            requiresRole: ["SUPER_ADMIN", "COMPANY_ADMIN"],
          },
        },
        {
          path: "admin/typography",
          name: "admin-typography",
          component: () => import("@/views/AdminTypographySettings.vue"),
          meta: {
            requiresAuth: true,
            requiresRole: ["SUPER_ADMIN", "COMPANY_ADMIN"],
          },
        },
        {
          path: "admin/region",
          name: "admin-region",
          component: () => import("@/views/AdminRegionSettings.vue"),
          meta: {
            requiresAuth: true,
            requiresRole: ["SUPER_ADMIN", "COMPANY_ADMIN"],
          },
        },
        {
          path: "admin/localization-health",
          name: "admin-l10n-health",
          component: () => import("@/views/AdminLocalizationHealth.vue"),
          meta: {
            requiresAuth: true,
            requiresRole: ["SUPER_ADMIN", "COMPANY_ADMIN"],
          },
        },
        {
          path: "admin/ai-suggestions",
          name: "admin-ai-suggestions",
          component: () => import("@/views/AdminTranslationSuggestions.vue"),
          meta: {
            requiresAuth: true,
            requiresRole: ["SUPER_ADMIN", "COMPANY_ADMIN"],
          },
        },
        {
          path: "admin/contributions",
          name: "admin-contributions",
          component: () => import("@/views/AdminTranslationContributions.vue"),
          meta: {
            requiresAuth: true,
            requiresRole: ["SUPER_ADMIN", "COMPANY_ADMIN"],
          },
        },
        {
          path: "admin/command-center",
          name: "admin-command-center",
          component: () => import("@/views/AdminCommandCenter.vue"),
          meta: {
            requiresAuth: true,
            requiresRole: ["SUPER_ADMIN", "COMPANY_ADMIN"],
            permission: "admin-command-center",
          },
        },
        {
          path: "admin/usage-analytics",
          name: "admin-usage-analytics",
          component: () => import("@/views/AdminUsageAnalytics.vue"),
          meta: {
            requiresAuth: true,
            requiresRole: ["SUPER_ADMIN", "COMPANY_ADMIN"],
            permission: "admin-usage-analytics",
          },
        },
        {
          path: "admin/audit-logs",
          name: "admin-audit-logs",
          component: () => import("@/views/AdminAuditLogs.vue"),
          meta: {
            requiresAuth: true,
            requiresRole: ["SUPER_ADMIN", "COMPANY_ADMIN"],
            permission: "admin-audit-logs",
          },
        },
      ],
    },

    // ── Unauthorized ───────────────────────────────────────────────────────
    {
      path: "/unauthorized",
      name: "unauthorized",
      component: () => import("@/views/UnauthorizedView.vue"),
    },

    // ── Fallback ──────────────────────────────────────────────────────────────
    { path: "/:pathMatch(.*)*", redirect: "/dashboard" },
  ],
});

// ── Auth Guard ────────────────────────────────────────────────────────────────
router.beforeEach(async (to) => {
  const auth = useAuthStore();

  // If a token exists but the user object is not yet populated (e.g. hard
  // refresh, direct URL entry, or bootstrap race), restore the session so
  // isAuthenticated evaluates against real state — not a stale null user.
  if (!auth.user && localStorage.getItem("access_token")) {
    await auth.tryRestoreSession();
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: "login", query: { redirect: to.fullPath } };
  }
  if ((to.name === "login" || to.name === "register") && auth.isAuthenticated) {
    return { name: "dashboard" };
  }
  // role guard
  const requiredRoles = to.meta.requiresRole as string[] | undefined;
  if (requiredRoles && requiredRoles.length) {
    const role = auth.user?.role;
    if (!role || !requiredRoles.includes(role)) {
      return { name: "unauthorized" };
    }
  }

  // permission guard
  const requiredPermission = to.meta.permission as string | undefined;
  if (requiredPermission) {
    if (!hasPermission(auth.user?.role, requiredPermission)) {
      return { name: "unauthorized" };
    }
  }

  // ── Route-level translation prefetch ───────────────────────────────────
  // Fire-and-forget: prefetch translations for the target route’s manifest.
  // Uses the standalone routePrefetch module (no Vue component context needed).
  // Non-blocking — if translation is slow, t() falls back to English.
  if (auth.isAuthenticated && to.path) {
    prefetchForRoute(to.path).catch(() => {
      /* silent — English fallback via resolveLocalizationKey */
    });
  }
});

export default router;
